import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  normalizeLegacyOrderSearch,
  orderListSearchSchema,
  ordersQueries,
  searchToFilter,
  type OrderListSearch,
} from '@/entities/order';
import { OrdersPage } from '@/pages/orders';

/** cpanel deep-link keys the old orders page consumed from its URL. */
const LEGACY_KEYS = ['order_id', 'status', 'courier_id', 'client_id', 'vendor_id'];

/**
 * Orders route (cpanel `/orders/all`) — the URL is the single source of truth
 * for tab / sub-tab / filters / sort / page.
 *   - cpanel's deep-link keys (`status`, `courier_id`, `client_id`,
 *     `vendor_id`) are rewritten onto the schema by a redirect; a cpanel
 *     `order_id` link opened the order drawer, so it redirects to the detail.
 *   - The loader prefetches non-blocking (status priority defaults off there;
 *     the page re-derives it with the per-user store).
 *   - Gated by the `orders.view` permission (`staticData`, enforced by the root guard).
 */
export const Route = createFileRoute('/orders/')({
  staticData: { permission: 'orders.view' },
  beforeLoad: ({ location }) => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
    const raw = Object.fromEntries(new URLSearchParams(location.searchStr));
    if (!LEGACY_KEYS.some((key) => key in raw)) return;
    if (raw.order_id) {
      // cpanel opened the order drawer for `order_id` — here, the detail page.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/orders/$orderId', params: { orderId: raw.order_id } });
    }
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/orders',
      search: orderListSearchSchema.parse(normalizeLegacyOrderSearch(raw)),
      replace: true,
    });
  },
  validateSearch: orderListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    // Best-effort and NON-BLOCKING: navigation never stalls on the list.
    void context.queryClient
      .prefetchQuery(ordersQueries.list(searchToFilter(deps)))
      .catch(() => undefined);
  },
  component: OrdersRoute,
});

function OrdersRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onParamsChange = (next: Partial<OrderListSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  return (
    <OrdersPage
      params={search}
      onParamsChange={onParamsChange}
      onViewOrder={(id) =>
        void navigate({ to: '/orders/$orderId', params: { orderId: String(id) } })
      }
      onOpenChat={(chat) => void navigate({ to: '/inbox', search: { chat } })}
    />
  );
}
